import { getDropboxDownloadLink, isDropboxUrl, isCanadaDropboxUrl } from '@/helpers/utils/dropboxLinks'
import { $getS3FileDownloadUrls, $getS3FileDownloadUrlsMultiFolders } from '@/api/fileManagement'
import type { S3DownloadParams } from '@/api/fileManagement'

export interface DownloadResult {
  status: 'success' | 'failed' | 'complete' | 'partial' | 'error'
  filename?: string
  link?: string
  error?: string
}

export interface DownloadSummary {
  status: 'complete' | 'partial' | 'error'
  total: number
  successCount: number
  failedCount: number
  results: Array<DownloadResult>
}

export interface ArtworkDownloadUI {
  active: boolean
  triggered: number
  total: number
  inProgress: number
  remaining: number
}

export interface DownloadStats {
  total: number
  fetched: number
  triggered: number
  failed: number
  inProgress: number
  remaining: number
}

export interface DownloadProgressEvent {
  link: string
  phase: 'fetching' | 'triggered' | 'failed'
  filename?: string
  error?: string
  stats: DownloadStats
}

export type DownloadLinkInput = string | {
  link: string
  baseName?: string
}

export interface FreshDownloadTarget {
  projectId: number | string
  fileId: string
  filename: string
  selectedItemIds?: string
  workOrderId?: number | string
}

const FRESH_DOWNLOAD_URL_EXPIRES_IN = 20
const FRESH_DOWNLOAD_MAX_ATTEMPTS = 3

const decodeUrlValue = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const sanitizeDownloadFilename = (filename?: string | null): string | null => {
  const normalized = filename
    ?.trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')

  return normalized || null
}

const getFilenameFromContentDisposition = (contentDisposition?: string | null): string | null => {
  if (!contentDisposition) {
    return null
  }

  const encodedFilenameMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8''|)([^;]+)/i)
  if (encodedFilenameMatch?.[1]) {
    return sanitizeDownloadFilename(
      decodeUrlValue(encodedFilenameMatch[1].trim().replace(/^["']|["']$/g, '')),
    )
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*("[^"]*"|'[^']*'|[^;]+)/i)
  if (filenameMatch?.[1]) {
    return sanitizeDownloadFilename(
      decodeUrlValue(filenameMatch[1].trim().replace(/^["']|["']$/g, '')),
    )
  }

  return null
}

const getFilenameFromUrlContentDisposition = (url: string): string | null => {
  try {
    const parsed = new URL(url)
    return getFilenameFromContentDisposition(parsed.searchParams.get('response-content-disposition'))
  } catch {
    return null
  }
}

/**
 * Get the clean filename from a URL (or fallback)
 * returns filename with extension if present
 */
export const getCleanFilenameFromUrl = (url: string): string => {
  try {
    const contentDispositionFilename = getFilenameFromUrlContentDisposition(url)
    if (contentDispositionFilename) {
      return contentDispositionFilename
    }

    const decoded = decodeUrlValue(url)
    const base = decoded.split('?')[0].split('#')[0]
    const name = sanitizeDownloadFilename(base.split('/').pop() || '')
    return name || `file-${Date.now()}`
  } catch {
    return `file-${Date.now()}`
  }
}

/**
 * Extract extension from a filename or url (without dot)
 */
const extFromFilename = (filename: string): string | null => {
  if (!filename) return null
  const parts = filename.split('.')
  if (parts.length <= 1) return null
  return parts.pop() || null
}

/**
 * Attempt to get extension from Content-Type header
 */
const extFromContentType = (contentType?: string | null): string | null => {
  if (!contentType) return null
  const parts = contentType.split('/')
  if (parts.length < 2) return null
  return parts[1].split(';')[0] || null
}

/**
 * Ensure filename has an extension; prefer ext from url first, then contentType
 */
const ensureFilenameWithExtension = (baseName: string, url: string, contentType?: string | null): string => {
  // try extract extension from url
  const urlFilename = getCleanFilenameFromUrl(url)
  const urlExt = extFromFilename(urlFilename)
  if (urlExt) return `${baseName}.${urlExt}`

  // fallback to content-type
  const ctExt = extFromContentType(contentType)
  if (ctExt) return `${baseName}.${ctExt}`

  // default
  return `${baseName}.bin`
}

const DOWNLOAD_TRIGGER_DELAY_MS = 300

// Iframe-based download trigger for Canada Dropbox URLs
const IFRAME_404_DETECTION_TIMEOUT_MS = 5_000

const triggerIframeDownload = (url: string): Promise<{ ok: boolean }> => {
  return new Promise(resolve => {
    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'display:none;width:0;height:0;'
    document.body.appendChild(iframe)

    let settled = false

    const cleanup = () => {
      try { document.body.removeChild(iframe) } catch { /* ignore */ }
    }

    // load fires -> Dropbox served an HTML error page -> file not found
    iframe.addEventListener('load', () => {
      if (settled) return
      settled = true
      cleanup()
      resolve({ ok: false })
    }, { once: true })

    // If load has not fired after the detection window -> real download occurred
    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      // Keep iframe alive long enough for the download to fully initiate,
      // then clean up.
      setTimeout(cleanup, 60_000)
      resolve({ ok: true })
    }, IFRAME_404_DETECTION_TIMEOUT_MS)

    // Start navigation
    iframe.src = url

    // If the iframe errors at the network level, treat as failed
    iframe.addEventListener('error', () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      cleanup()
      resolve({ ok: false })
    }, { once: true })
  })
}

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
}

const triggerUrlDownload = (url: string, filename?: string): void => {
  const anchor = document.createElement('a')
  anchor.href = url
  if (filename) {
    anchor.download = filename
  }
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

const downloadUrlSequence = async (downloads: Array<{ url: string, filename?: string }>): Promise<void> => {
  for (const item of downloads) {
    triggerUrlDownload(item.url, item.filename)
    await delay(DOWNLOAD_TRIGGER_DELAY_MS)
  }
}

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

const normalizeDownloadLinkInput = (input: DownloadLinkInput): { link: string, baseName?: string } => {
  if (typeof input === 'string') {
    return { link: input }
  }

  return {
    link: input.link,
    baseName: input.baseName,
  }
}

const isS3PresignedUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return parsed.searchParams.has('X-Amz-Signature')
      || parsed.searchParams.has('X-Amz-Algorithm')
      || parsed.hostname.includes('amazonaws.com')
      || parsed.hostname.includes('minio')
      || parsed.hostname.includes('localhost')
  } catch {
    return false
  }
}

// Progress Tracker
class DownloadProgressTracker {
  total: number
  fetched = 0
  triggered = 0
  failed = 0

  constructor(total: number) {
    this.total = total
  }

  markFetching() { this.fetched++ }
  markTriggered() { this.triggered++ }
  markFailed() { this.failed++ }

  getStats(): DownloadStats {
    const inProgress = this.fetched - this.triggered - this.failed

    return {
      total: this.total,
      fetched: this.fetched,
      triggered: this.triggered,
      failed: this.failed,
      inProgress,
      remaining: this.total - this.triggered - this.failed,
    }
  }
}

// Serial trigger queue
type TriggerFn = () => Promise<void> | void

class DownloadTriggerQueue {
  private queue: TriggerFn[] = []
  private running = false

  enqueue(trigger: TriggerFn): void {
    this.queue.push(trigger)
    if (!this.running) this.drain()
  }

  private async drain(): Promise<void> {
    this.running = true
    while (this.queue.length > 0) {
      const next = this.queue.shift()!
      await next()
      await delay(DOWNLOAD_TRIGGER_DELAY_MS)
    }
    this.running = false
  }
}

// Core: fetch (or schedule) a single link and enqueue its download trigger
const fetchAndEnqueue = async (
  link: string,
  queue: DownloadTriggerQueue,
  tracker: DownloadProgressTracker,
  baseName?: string,
  onProgress?: (event: DownloadProgressEvent) => void
): Promise<DownloadResult> => {
  const normalizedLink = isDropboxUrl(link) ? getDropboxDownloadLink(link) : link
  const filenameFromUrl = getCleanFilenameFromUrl(normalizedLink)

  tracker.markFetching()
  onProgress?.({ link, phase: 'fetching', stats: tracker.getStats() })

  // Canada Dropbox path - Cannot fetch() due to CORS
  // Use iframe trigger and detect 404 via the load event (fires on error page, does not fire on file download)
  if (isCanadaDropboxUrl(normalizedLink)) {
    queue.enqueue(() => {
      triggerIframeDownload(normalizedLink).then(({ ok }) => {
        if (ok) {
          tracker.markTriggered()
          onProgress?.({
            link,
            phase: 'triggered',
            filename: filenameFromUrl,
            stats: tracker.getStats(),
          })
        } else {
          tracker.markFailed()
          onProgress?.({
            link,
            phase: 'failed',
            error: 'HTTP 404',
            stats: tracker.getStats(),
          })
        }
      })
    })

    return { status: 'success', link: normalizedLink, filename: filenameFromUrl }
  }

  if (isS3PresignedUrl(normalizedLink)) {
    queue.enqueue(() => {
      triggerUrlDownload(normalizedLink, baseName)

      tracker.markTriggered()
      onProgress?.({
        link,
        phase: 'triggered',
        filename: baseName ?? filenameFromUrl,
        stats: tracker.getStats(),
      })
    })

    return { status: 'success', link: normalizedLink, filename: baseName ?? filenameFromUrl }
  }

  // Standard path: fetch blob, then trigger anchor download.
  try {
    const response = await fetch(normalizedLink)

    if (!response.ok) {
      const error = `HTTP ${response.status}`

      tracker.markFailed()
      onProgress?.({ link, phase: 'failed', error, stats: tracker.getStats() })
      return { status: 'failed', link: normalizedLink, error }
    }

    const contentType = response.headers.get('content-type')

    const base =
      baseName ??
      (filenameFromUrl.includes('.')
        ? filenameFromUrl.split('.').slice(0, -1).join('.')
        : filenameFromUrl || `file-${Date.now()}`)

    const finalFilename = ensureFilenameWithExtension(base, normalizedLink, contentType)
    const blob = await response.blob()

    queue.enqueue(() => {
      triggerBlobDownload(blob, finalFilename)

      tracker.markTriggered()
      onProgress?.({
        link,
        phase: 'triggered',
        filename: finalFilename,
        stats: tracker.getStats(),
      })
    })

    return { status: 'success', link: normalizedLink, filename: finalFilename }
  } catch (err: any) {
    const error = err instanceof Error ? err.message : String(err)

    tracker.markFailed()
    onProgress?.({ link, phase: 'failed', error, stats: tracker.getStats() })
    return { status: 'failed', link, error }
  }
}

// Public API
export const downloadFileUsingLink = async (
  link: string,
  baseName?: string,
  onProgress?: (event: DownloadProgressEvent) => void
): Promise<DownloadResult> => {
  const queue = new DownloadTriggerQueue()
  const tracker = new DownloadProgressTracker(1)

  return fetchAndEnqueue(link, queue, tracker, baseName, onProgress)
}

/**
 * Orchestrates bulk file downloads using context-specific throttling behavior.
 * * NOTE ON BROWSER STREAM BEHAVIOR:
 * When processing raw S3 attachment links sequentially without a delay or an in-memory buffer,
 * the browser interprets rapid navigation requests as frame overrides, actively aborting prior
 * network streams. To bypass this limitation, `isS3Download: true` enforces an explicit, sequential
 * synchronous network loop that waits for each asset's payload bytes to be 100% committed to memory
 * before authorizing the subsequent request pipeline.
 */
export const downloadAllFilesUsingLinks = async (
  links: DownloadLinkInput[],
  baseName?: string,
  onProgress?: (event: DownloadProgressEvent) => void,
  isS3Download = false
): Promise<DownloadSummary> => {
  if (!links?.length) {
    return { status: 'complete', total: 0, successCount: 0, failedCount: 0, results: [] }
  }

  const queue = new DownloadTriggerQueue()
  const tracker = new DownloadProgressTracker(links.length)
  let settled: PromiseSettledResult<DownloadResult>[]

  // Normalizes polymorphic string or object inputs into a standard signature
  const parseInput = (input: DownloadLinkInput) => {
    if (typeof input === 'string') return { link: input, linkBaseName: undefined }
    return { link: input.link, linkBaseName: input.baseName }
  }

  if (isS3Download) {
    settled = []

    // SEQUENTIAL PATTERN: Freezes loop execution on each active item.
    // Prevents browser navigation interception/request cancellations during massive bulk updates.
    for (const linkInput of links) {
      const { link, linkBaseName } = parseInput(linkInput)
      const finalName = linkBaseName ?? baseName
      const filename = finalName ?? getCleanFilenameFromUrl(link)

      tracker.markFetching()
      onProgress?.({
        link,
        phase: 'fetching',
        filename,
        stats: tracker.getStats(),
      })

      try {
        const result = await downloadFileAsBlobWithProgress(link, filename)
        const completedResult = { ...result, filename: result.filename ?? filename }

        tracker.markTriggered()
        onProgress?.({
          link,
          phase: 'triggered',
          filename: completedResult.filename,
          stats: tracker.getStats(),
        })

        settled.push({ status: 'fulfilled', value: completedResult })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)

        tracker.markFailed()
        onProgress?.({
          link,
          phase: 'failed',
          filename,
          error: errorMessage,
          stats: tracker.getStats(),
        })

        settled.push({
          status: 'fulfilled',
          value: { status: 'failed', link, filename, error: errorMessage },
        })
      }
    }
  } else {
    // CONCURRENT PATTERN: Leveraged for standard non-S3 file downloads
    settled = await Promise.allSettled(
      links.map(linkInput => {
        const { link, linkBaseName } = parseInput(linkInput)
        return fetchAndEnqueue(link, queue, tracker, linkBaseName ?? baseName, onProgress)
      })
    )
  }

  // Aggregate operational metrics while preserving contextual data (like target URLs) on failure paths
  const results: DownloadResult[] = settled.map((s, index) => {
    if (s.status === 'fulfilled') return s.value
    const msg = s.reason instanceof Error ? s.reason.message : JSON.stringify(s.reason)
    return { status: 'failed', link: parseInput(links[index]).link || 'unknown', error: msg }
  })

  const successCount = results.filter(r => r.status === 'success').length
  const failedCount = results.filter(r => r.status === 'failed').length
  const overallStatus = failedCount === 0 ? 'complete' : failedCount === links.length ? 'error' : 'partial'

  return { status: overallStatus, total: links.length, successCount, failedCount, results }
}

/**
 * Downloads a remote network asset entirely into local application memory (Blob)
 * prior to dispatching the browser's operating system write signal.
 * * Because local object data pools (`blob:` protocols) evaluate synchronously on the DOM,
 * calling `.click()` on these assets bypasses popup blocker warnings and guarantees that
 * subsequent loops do not cancel ongoing network operations.
 */
const downloadFileAsBlobWithProgress = (
  url: string,
  filename?: string,
): Promise<DownloadResult> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'

    // Executes immediately following full buffer transmission
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const blob = xhr.response
        const blobUrl = window.URL.createObjectURL(blob)
        const fallbackFilename = getCleanFilenameFromUrl(url)
        const urlExt = extFromFilename(fallbackFilename)

        // Transient DOM construction to invoke file systems writing hooks safely
        const anchor = document.createElement('a')
        anchor.href = blobUrl

        let downloadFilename = filename || fallbackFilename

        if (filename && !extFromFilename(filename) && urlExt) {
          downloadFilename = `${filename}.${urlExt}`
        }

        anchor.download = downloadFilename

        document.body.appendChild(anchor)
        anchor.click()

        // Garbarge collect allocated objects and DOM chains to mitigate memory bloat
        document.body.removeChild(anchor)
        window.URL.revokeObjectURL(blobUrl)

        resolve({ status: 'success', link: url, filename: downloadFilename })
      } else {
        reject(new Error(`HTTP Status Code: ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      const error = new Error('Network request failed') as Error & { isCorsOpaque?: boolean }
      // S3 can complete the transport with HTTP 200 while the browser blocks
      // JavaScript from reading it because the bucket has no matching CORS
      // policy. Chrome may still expose that status as 200 in DevTools even
      // though it invokes `onerror`; the presigned URL remains valid for a
      // normal navigation.
      error.isCorsOpaque = true
      reject(error)
    }
    xhr.send()
  })
}

/**
 * Download S3-backed files one by one, obtaining a new short-lived URL for
 * each attempt.  This prevents a large batch from consuming URLs that were
 * already close to expiry when the batch started.
 */
export const downloadFreshFiles = async (
  targets: FreshDownloadTarget[],
  onProgress?: (event: DownloadProgressEvent) => void,
): Promise<DownloadSummary> => {
  if (!targets.length) {
    return { status: 'complete', total: 0, successCount: 0, failedCount: 0, results: [] }
  }

  const tracker = new DownloadProgressTracker(targets.length)
  const results: DownloadResult[] = []

  for (const target of targets) {
    const progressLink = `file:${target.fileId}`
    let lastError = 'Unable to download file'
    let completedResult: DownloadResult | null = null
    let url: string | undefined

    tracker.markFetching()
    onProgress?.({
      link: progressLink,
      phase: 'fetching',
      filename: target.filename,
      stats: tracker.getStats(),
    })

    for (let attempt = 0; attempt < FRESH_DOWNLOAD_MAX_ATTEMPTS; attempt++) {
      try {
        const data = await $getS3FileDownloadUrls(
          target.projectId,
          {
            file_id: target.fileId,
            intent: 'download',
            selected_item_ids: target.selectedItemIds,
            work_order_id: target.workOrderId,
          },
          FRESH_DOWNLOAD_URL_EXPIRES_IN,
        )
        url = data?.url ?? data?.download_url
        if (!url) {
          throw new Error('No download URL returned')
        }

        completedResult = await downloadFileAsBlobWithProgress(url, target.filename)
        break
      } catch (error) {
        lastError = error instanceof Error ? error.message : String(error)

        if ((error as Error & { isCorsOpaque?: boolean })?.isCorsOpaque && url) {
          // Direct navigation does not require CORS. This fallback is limited
          // to opaque browser errors; normal HTTP/network errors still retry
          // with a newly-issued presigned URL.
          triggerUrlDownload(url, target.filename)
          await delay(DOWNLOAD_TRIGGER_DELAY_MS)
          completedResult = { status: 'success', link: url, filename: target.filename }
          break
        }
      }
    }

    if (completedResult) {
      tracker.markTriggered()
      onProgress?.({
        link: completedResult.link ?? progressLink,
        phase: 'triggered',
        filename: completedResult.filename ?? target.filename,
        stats: tracker.getStats(),
      })
      results.push(completedResult)
      continue
    }

    tracker.markFailed()
    onProgress?.({
      link: progressLink,
      phase: 'failed',
      filename: target.filename,
      error: lastError,
      stats: tracker.getStats(),
    })
    results.push({
      status: 'failed',
      link: progressLink,
      filename: target.filename,
      error: lastError,
    })
  }

  const successCount = results.filter(result => result.status === 'success').length
  const failedCount = results.length - successCount
  return {
    status: failedCount === 0 ? 'complete' : successCount === 0 ? 'error' : 'partial',
    total: targets.length,
    successCount,
    failedCount,
    results,
  }
}

// ---------------------------------------------------------------------------
// S3-aware download helpers
// ---------------------------------------------------------------------------

/**
 * S3 folder that maps to an item file type.
 */
export type S3FileType = 'artwork' | 'prepress_proof' | 'installation' | 'scope'

const S3_FOLDER_MAP: Record<S3FileType, string> = {
  artwork: 'ARTWORK',
  prepress_proof: 'PREPRESS_PROOF',
  installation: 'INSTALLATION',
  scope: 'SCOPE',
}

/**
 * Download a file from S3 using a fresh presigned URL.
 *
 * @param projectId  The project (campaign) ID — used as top-level S3 prefix.
 * @param code       Item code (e.g. "LAR-SP-TNL-0005") — the S3 filename stem.
 * @param fileType   'artwork' | 'prepress_proof'
 * @param ext        File extension WITHOUT leading dot (e.g. "pdf").  When
 *                   omitted the presigned URL will be fetched for ".pdf" by
 *                   default, since ARTWORK/PREPRESS_PROOF folders store code-
 *                   only filenames (code + single extension).
 */
export const downloadFromS3 = async (
  projectId: number | string,
  code: string,
  fileType: S3FileType,
  ext = 'pdf',
): Promise<DownloadSummary> => {
  const folder = S3_FOLDER_MAP[fileType]

  try {
    const response = await $getS3FileDownloadUrls(projectId, { folder, code, intent: 'download' })
    const presignedItems = Array.isArray(response?.results) ? response.results : [response]
    const downloads = presignedItems
      .map((item: any) => ({
        url: item?.download_url,
        filename:
          item?.file
            ? `${item.file.name}${item.file.extension || ''}`
            : item?.filename || getCleanFilenameFromUrl(item?.download_url),
      }))
      .filter((item: any): item is { url: string, filename?: string } => typeof item.url === 'string' && !!item.url)

    if (!downloads.length) {
      return { status: 'complete', total: 0, successCount: 0, failedCount: 0, results: [] }
    }

    return await downloadAllFilesUsingLinks(downloads.map((l: { url: string, filename?: string }) => ({link: l.url, baseName: l.filename})), undefined, undefined, true)
  } catch (err: any) {
    return { status: 'error', total: 0, successCount: 0, failedCount: 0, results: [] }
  }
}

/**
 * Resolve the preferred download source for an artwork/proof field, then
 * trigger the download.
 *
 * Priority:
 *  1. If `isInS3` is true → always download via signed S3 URL.
 *  2. Else if `dropboxLink` is provided → use existing Dropbox download flow.
 *
 * Returns a DownloadResult.
 */
export const downloadArtworkOrProof = async (
  projectId?: number | string,
  code?: string,
  fileType?: S3FileType,
  ext?: string,
): Promise<DownloadSummary> => {
  if (projectId && code && fileType) {
    return downloadFromS3(projectId, code, fileType, ext)
  }

  return { status: 'error', total: 0, successCount: 0, failedCount: 0, results: [] }
}

/**
 * Download one exact S3 object by key, or all files matching a code across
 * the provided folders, using fresh presigned URLs.
 *
 * @param projectId The project (campaign) ID used as the top-level S3 prefix.
 * @param params     Either `{ key }` or `{ code, folders }`.
 */
export const downloadFromS3MultiFolders = async (
  projectId: number | string,
  params: S3DownloadParams,
): Promise<DownloadSummary> => {
  try {
    const response = await $getS3FileDownloadUrlsMultiFolders(
      projectId,
      { ...params, intent: 'download' },
    )
    const presignedItems = 'results' in response ? response.results : [response]
    const downloads = presignedItems
      .map(item => ({
        url: item.download_url,
        filename:
          ('file' in item && item.file ? `${item.file.name}${item.file.extension || ''}` : undefined)
          || ('filename' in item && item.filename)
          || getCleanFilenameFromUrl(item.key || item.download_url),
      }))
      .filter(
        (item): item is { url: string, filename: string } =>
          typeof item.url === 'string' && Boolean(item.url),
      )

    if (!downloads.length) {
      return {
        status: 'complete',
        total: 0,
        successCount: 0,
        failedCount: 0,
        results: [],
      }
    }

    return await downloadAllFilesUsingLinks(
      downloads.map(item => ({
        link: item.url,
        baseName: item.filename,
      })),
      undefined,
      undefined,
      true,
    )
  }
  catch {
    return {
      status: 'error',
      total: 0,
      successCount: 0,
      failedCount: 0,
      results: [],
    }
  }
}
